/* eslint-disable @typescript-eslint/no-explicit-any */
// contexts/PaymentContext.tsx - Enhanced version with memoization
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { Plan, PaymentIntent } from "../types/stripe";

// Types
export type BillingPeriod = "monthly" | "annual";
export type PaymentMethod = "mpesa" | "card" | "paypal";

interface PaymentState {
  user: any;
  selectedPlan: Plan | null;
  billingPeriod: BillingPeriod;
  paymentMethod: PaymentMethod;
  mpesaPhoneNumber: string;
  isLoading: boolean;
  error: string | null;
  paymentIntent: PaymentIntent | null;
  isYearlyDiscountApplied: boolean;
  calculatedPrice: number;
}

type PaymentAction =
  | { type: "SET_PLAN"; payload: { plan: Plan; billingPeriod: BillingPeriod } }
  | { type: "SET_PAYMENT_METHOD"; payload: PaymentMethod }
  | { type: "SET_MPESA_PHONE"; payload: string }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_PAYMENT_INTENT"; payload: PaymentIntent | null }
  | { type: "RESET_PAYMENT" }
  | { type: "TOGGLE_BILLING_PERIOD" }
  | { type: "SET_BILLING_PERIOD"; payload: BillingPeriod }
  | { type: "CALCULATE_PRICE" }
  | { type: "SET_USER"; payload: any };

interface PaymentContextType {
  state: PaymentState;
  dispatch: React.Dispatch<PaymentAction>;
  setPlan: (plan: Plan, billingPeriod: BillingPeriod) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setMpesaPhone: (phone: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setPaymentIntent: (intent: PaymentIntent | null) => void;
  resetPayment: () => void;
  toggleBillingPeriod: () => void;
  setBillingPeriod: (period: BillingPeriod) => void;
  calculateFinalPrice: (plan: Plan, billingPeriod: BillingPeriod) => number;
  isPlanSelected: (planId: string) => boolean;
  clearPayment: () => void;
  setUser: (user: any) => void;
}

// Initial state
const initialState: PaymentState = {
  selectedPlan: null,
  billingPeriod: "monthly",
  paymentMethod: "mpesa",
  mpesaPhoneNumber: "",
  isLoading: false,
  error: null,
  paymentIntent: null,
  isYearlyDiscountApplied: false,
  calculatedPrice: 0,
  user: null,
};

// Helper functions
const calculatePrice = (plan: Plan, billingPeriod: BillingPeriod): number => {
  if (billingPeriod === "annual") {
    return plan.annualPrice || plan.price * 12 * 0.8;
  }
  return plan.monthlyPrice || plan.price;
};

const hasYearlyDiscount = (billingPeriod: BillingPeriod): boolean => {
  return billingPeriod === "annual";
};

// Reducer
const paymentReducer = (
  state: PaymentState,
  action: PaymentAction,
): PaymentState => {
  switch (action.type) {
    case "SET_PLAN": {
      const calculatedPrice = calculatePrice(
        action.payload.plan,
        action.payload.billingPeriod,
      );
      return {
        ...state,
        selectedPlan: action.payload.plan,
        billingPeriod: action.payload.billingPeriod,
        calculatedPrice,
        isYearlyDiscountApplied: hasYearlyDiscount(
          action.payload.billingPeriod,
        ),
        error: null,
      };
    }

    case "SET_PAYMENT_METHOD":
      return {
        ...state,
        paymentMethod: action.payload,
        error: null,
      };

    case "SET_MPESA_PHONE":
      return {
        ...state,
        mpesaPhoneNumber: action.payload,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error,
      };

    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case "SET_PAYMENT_INTENT":
      return {
        ...state,
        paymentIntent: action.payload,
        isLoading: false,
        error: null,
      };

    case "SET_USER":
      return {
        ...state,
        user: action.payload,
      };

    case "TOGGLE_BILLING_PERIOD": {
      if (!state.selectedPlan) return state;

      const newBillingPeriod: BillingPeriod =
        state.billingPeriod === "monthly" ? "annual" : "monthly";
      const newCalculatedPrice = calculatePrice(
        state.selectedPlan,
        newBillingPeriod,
      );

      return {
        ...state,
        billingPeriod: newBillingPeriod,
        calculatedPrice: newCalculatedPrice,
        isYearlyDiscountApplied: hasYearlyDiscount(newBillingPeriod),
      };
    }

    case "SET_BILLING_PERIOD": {
      if (!state.selectedPlan)
        return { ...state, billingPeriod: action.payload };

      const updatedCalculatedPrice = calculatePrice(
        state.selectedPlan,
        action.payload,
      );

      return {
        ...state,
        billingPeriod: action.payload,
        calculatedPrice: updatedCalculatedPrice,
        isYearlyDiscountApplied: hasYearlyDiscount(action.payload),
      };
    }

    case "CALCULATE_PRICE":
      if (!state.selectedPlan) return state;

      return {
        ...state,
        calculatedPrice: calculatePrice(
          state.selectedPlan,
          state.billingPeriod,
        ),
        isYearlyDiscountApplied: hasYearlyDiscount(state.billingPeriod),
      };

    case "RESET_PAYMENT":
      return initialState;

    default:
      return state;
  }
};

// Create Context
const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// Provider Component
interface PaymentProviderProps {
  children: ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);

  // Memoize all action creators
  const setPlan = useCallback((plan: Plan, billingPeriod: BillingPeriod) => {
    dispatch({ type: "SET_PLAN", payload: { plan, billingPeriod } });
  }, []);

  const setPaymentMethod = useCallback((method: PaymentMethod) => {
    dispatch({ type: "SET_PAYMENT_METHOD", payload: method });
  }, []);

  const setMpesaPhone = useCallback((phone: string) => {
    dispatch({ type: "SET_MPESA_PHONE", payload: phone });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  const setPaymentIntent = useCallback((intent: PaymentIntent | null) => {
    dispatch({ type: "SET_PAYMENT_INTENT", payload: intent });
  }, []);

  const setUser = useCallback((user: any) => {
    dispatch({ type: "SET_USER", payload: user });
  }, []);

  const resetPayment = useCallback(() => {
    dispatch({ type: "RESET_PAYMENT" });
  }, []);

  const toggleBillingPeriod = useCallback(() => {
    if (state.selectedPlan) {
      dispatch({ type: "TOGGLE_BILLING_PERIOD" });
    }
  }, [state.selectedPlan]);

  const setBillingPeriod = useCallback((period: BillingPeriod) => {
    dispatch({ type: "SET_BILLING_PERIOD", payload: period });
  }, []);

  const calculateFinalPrice = useCallback(
    (plan: Plan, billingPeriod: BillingPeriod): number => {
      return calculatePrice(plan, billingPeriod);
    },
    [],
  );

  const isPlanSelected = useCallback(
    (planId: string): boolean => {
      return state.selectedPlan?.id === planId;
    },
    [state.selectedPlan],
  );

  const clearPayment = resetPayment;

  // Memoize the entire context value
  const value = useMemo(
    () => ({
      state,
      dispatch,
      setPlan,
      setPaymentMethod,
      setMpesaPhone,
      setLoading,
      setError,
      setPaymentIntent,
      setUser,
      resetPayment,
      toggleBillingPeriod,
      setBillingPeriod,
      calculateFinalPrice,
      isPlanSelected,
      clearPayment,
    }),
    [
      state,
      setPlan,
      setPaymentMethod,
      setMpesaPhone,
      setLoading,
      setError,
      setPaymentIntent,
      setUser,
      resetPayment,
      toggleBillingPeriod,
      setBillingPeriod,
      calculateFinalPrice,
      isPlanSelected,
      clearPayment,
    ],
  );

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};

// Custom hook to use the payment context
// eslint-disable-next-line react-refresh/only-export-components
export const usePaymentContext = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error("usePaymentContext must be used within a PaymentProvider");
  }
  return context;
};
